import { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Radio, RadioGroup, FormControlLabel, FormControl,
  FormLabel, Checkbox, FormGroup, Alert, CircularProgress,
  Typography, Box,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { adminAgentsAPI } from '../../../services/api'

export default function ReseedDialog({ open, onClose, onComplete }) {
  const [step, setStep] = useState('mode')
  const [mode, setMode] = useState('idempotent')
  const [availableAgents, setAvailableAgents] = useState([])
  const [selectedAgents, setSelectedAgents] = useState([])
  const [loadingAgents, setLoadingAgents] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) {
      setStep('mode')
      setMode('idempotent')
      setSelectedAgents([])
      setError(null)
      setBusy(false)
    }
  }, [open])

  const handleNextToAgents = async () => {
    setLoadingAgents(true)
    setError(null)
    try {
      const data = await adminAgentsAPI.listSeeders()
      const agents = data.agents ?? []
      setAvailableAgents(agents)
      setSelectedAgents([...agents])
      setStep('agents')
    } catch {
      setError('Failed to load seeder agents. Check server logs.')
    } finally {
      setLoadingAgents(false)
    }
  }

  const handleSelectAll = (checked) => {
    setSelectedAgents(checked ? [...availableAgents] : [])
  }

  const handleToggleAgent = (name, checked) => {
    setSelectedAgents(prev =>
      checked ? [...prev, name] : prev.filter(n => n !== name)
    )
  }

  const handleConfirm = async () => {
    setBusy(true)
    setError(null)
    try {
      const options = mode === 'forced'
        ? { force: true, agent_names: selectedAgents }
        : undefined
      const result = await adminAgentsAPI.reseedFromJson(options)
      onComplete(result)
    } catch {
      setError('Re-seed failed. Check server logs.')
      setBusy(false)
    }
  }

  const allSelected = availableAgents.length > 0 && selectedAgents.length === availableAgents.length
  const someSelected = selectedAgents.length > 0 && !allSelected

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {step === 'mode' && (
        <>
          <DialogTitle>Re-seed from JSON</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <FormControl>
              <FormLabel sx={{ mb: 1 }}>Choose re-seed mode</FormLabel>
              <RadioGroup value={mode} onChange={e => setMode(e.target.value)}>
                <FormControlLabel
                  value="idempotent"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Idempotent</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Skip agents that already have a canvas. Safe to run any time.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="forced"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Forced</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Overwrite canvas and prompt edits for selected agents.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            {mode === 'idempotent' ? (
              <Button variant="contained" onClick={handleConfirm} disabled={busy}>
                {busy ? 'Re-seeding…' : 'Re-seed'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNextToAgents} disabled={loadingAgents}>
                {loadingAgents ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Next
              </Button>
            )}
          </DialogActions>
        </>
      )}

      {step === 'agents' && (
        <>
          <DialogTitle>Select agents to overwrite</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              This will permanently overwrite all canvas and prompt edits for the
              selected agents. This cannot be undone.
            </Alert>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={e => handleSelectAll(e.target.checked)}
                  />
                }
                label={<Typography fontWeight={600}>Select all</Typography>}
              />
              {availableAgents.map(name => (
                <FormControlLabel
                  key={name}
                  sx={{ ml: 2 }}
                  control={
                    <Checkbox
                      checked={selectedAgents.includes(name)}
                      onChange={e => handleToggleAgent(name, e.target.checked)}
                    />
                  }
                  label={name}
                />
              ))}
            </FormGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep('mode')}>Back</Button>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirm}
              disabled={busy || selectedAgents.length === 0}
            >
              {busy ? 'Re-seeding…' : `Re-seed ${selectedAgents.length} agent(s)`}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
